import {createAsyncThunk} from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
// Local helpers for SSO and simple utils to avoid pulling broader utilities
import { resetAdminUserContext } from '../../admin/state/users/admin-slice';
import {CookieExpiration} from '../../shared/enums';
import {RootState} from '../../shared/state/root-reducer';
import {GetError} from '../model/application-status';
import {ChangePasswordState} from '../model/change-password-state';
import {Credentials} from '../model/credentials';
import {IdProviderFormat, SamlUrl, sampleIdDB, Tokens} from '../services/SSO';
import {getDefaultUserContextState} from '../model/user-context-state';
import {
  authService, AuthView,
  changeAuthState,
  GENERIC_AUTHENICATION_ERROR,
  INVALID_DOMAIN_ERROR,
  VERIFICATIONCODE_AUTHENICATION_ERROR,
} from '../services/authorization-service';
import {userService} from '../services/user-service';
import {resetUserContext, showStatus} from './user-slice';
import HttpInterceptor from '../../shared/Http/http-interceptor';
import {signIn, SignInOutput} from 'aws-amplify/auth';
import {
  getClients,
} from '../../implementation-specialist/state/clients/implementation-clients-thunk';
import {configSettings} from '../../shared/configuration/config-settings';
import _ from 'lodash';
// removed patient helper dependency for reports-only app

export const logUserIn = createAsyncThunk(
  'logUserIn',
  async (credentials: Credentials, thunkAPI) => {

      clearAllState(); // ensures you can switch from logging in as a patient or as other/adminif (typeof credentials.password !== "undefined") {
      if (typeof credentials.password !== "undefined") {
          const isUserAuthenticated = await authService.login(credentials.email, credentials.password);

          const loginResult = getDefaultUserContextState();

          // When the user has been authenticated trigger call to get user profile
          if (isUserAuthenticated) {
              const userProfileStatus = await userService.getUserProfileWrapper({thunkAPI});
              // When Profile is missing log user out and show error
              if (userProfileStatus.userProfileNotFound) {
                  loginResult.applicationStatus = GetError(GENERIC_AUTHENICATION_ERROR);
                  thunkAPI.dispatch(logUserOut());
              } else {
                  loginResult.userProfile = userProfileStatus.userProfile;
                  loginResult.userIsAuthenticated = true;
                  loginResult.onLoginScreen = false;
                  loginResult.homeRoute = userProfileStatus.userProfile.defaultRoute;
                  loginResult.clearForm = true;
                  Cookies.set("userStatus", {isActive: true}, {expires: CookieExpiration.days});
              }
          } else {
            const authenicatedUser: SignInOutput = await signIn({
              username: credentials.email,
              password: credentials.password
            });
            const checkForChallenge: boolean = !!authenicatedUser?.nextStep?.signInStep;
            if (!checkForChallenge) {
              // don't set application status to error if we're currently redirecting for a challenge
              loginResult.applicationStatus = GetError(GENERIC_AUTHENICATION_ERROR);
            }
          }
          return loginResult;
      }
      // Allow for custom error
      else if (credentials.error){
          const loginResult = getDefaultUserContextState();
          loginResult.applicationStatus = GetError(credentials.error);

          return loginResult;
      }
      else {
          const loginResult = getDefaultUserContextState();
          loginResult.applicationStatus = GetError("Passwords are required");

          return loginResult;
      }

  }

);

export const getProfileState = createAsyncThunk(
    'getProfileState',
    async(tokens: Tokens, thunkAPI) =>{
        clearAllState(); // ensures you can switch from logging in as a patient or as other/admin
        const loginResult = getDefaultUserContextState();

            const userProfileStatus = await userService.getUserProfileWrapper({isSSO: true, tokens, thunkAPI});
            // When Profile is missing log user out and show error
            if (userProfileStatus.userProfileNotFound) {
                thunkAPI.dispatch(clearAllState());
                // Redirects to home with error as query
                window.location.replace("/?error=404");
            } else {
                loginResult.userProfile = userProfileStatus.userProfile;
                loginResult.userIsAuthenticated = true;
                loginResult.onLoginScreen = false;
                loginResult.homeRoute = userProfileStatus.userProfile.defaultRoute;
                loginResult.userEmail = userProfileStatus.userProfile.userName;
                loginResult.clearForm = true;
                Cookies.set("userStatus", {isActive: true}, {expires: CookieExpiration.days});
            }

        return loginResult;
    }

);

export const refreshToken = createAsyncThunk(
  'refreshToken', async () => {
    return await authService.fetchSecurityToken();
  }
)
export const generateToken = createAsyncThunk(
    'generateToken',
    async(code : string, thunkAPI) =>{
        const tokens: Tokens = await userService.getTokensFromCode(code);
        HttpInterceptor(true, tokens);
        return thunkAPI.dispatch(getProfileState(tokens));
    }
)

const includesIDP = (email_domain : string, allProviders : IdProviderFormat[]) : boolean  =>{

  for(const provider of allProviders) {
    if (provider.idp_identifier === email_domain) {
      return true;
    }
  }
  return false;

}

// Generates a saml redirect link
// Can take in no parameters or custom full or partial saml options
const generateSamlUrl = (options : SamlUrl = {}) : string => {
  const defaults ={
    cognitoDomain : `finpass-${configSettings.env}.auth.us-east-1.amazoncognito.com`,
    responseType : 'code',
    IDP :{
      type : "idp_identifier",
      value : "finpay.net"
    },
    clientID : configSettings.aws_userPoolWebClientId,
    redirectURI : `${configSettings.env !== "prod" ? configSettings.home_uri : "https://admin.finpay.net"}`
  }
  const values = _.merge({}, defaults, options);
  return `https://${values.cognitoDomain}/oauth2/authorize?response_type=${values.responseType}&${values.IDP.type}=${values.IDP.value}&client_id=${values.clientID}&redirect_uri=${values.redirectURI}`;

}

export const ssoRedirect = createAsyncThunk(
    'ssoRedirect',
    async(credentials : Credentials, thunkAPI) =>{
        const email_domain : string = credentials.email.split('@')[1];
      if(!credentials.password) {
        if (includesIDP(email_domain, sampleIdDB)) {
          const url : string = generateSamlUrl({IDP: {value: email_domain}});
          window.location.replace(url);
          // to run with a custom redirect link
          // ({IDP: {value: email_domain}, redirectURI: "http://localhost:3000"});
        } else {
          return thunkAPI.rejectWithValue(INVALID_DOMAIN_ERROR);
        }
      }

    }
)

export const checkForCode = createAsyncThunk(
    'checkCode',
    async(param : void, thunkAPI) => {
        HttpInterceptor(false);
        const url :URL = new URL(window.location.href);
        const params : URLSearchParams = new URLSearchParams(url.search);
        if(params.has('code')){
            const code : any = params.get('code');
            return thunkAPI.dispatch(generateToken(code));
        }
        if(params.has('error')){
            const error_string : any = params.get('error');
            const error_code : number = parseInt(error_string);
            const getErrorByCode = (code: number) => (code === 404 ? 'Profile not found' : '');
            alert(`Error ${error_code}: ${getErrorByCode(error_code)} \n\n${GENERIC_AUTHENICATION_ERROR}`);
            window.location.replace("/");
        }
    }
)

export const logUserOut = createAsyncThunk(
    'logUserOut',
    async (param: void, thunkAPI) => {
      thunkAPI.dispatch(clearAllState());
      const response = await authService.logout();
      return response;
    }
)


export const clearAllState = createAsyncThunk(
  'clearAllState',
  async (param: void, thunkAPI) => {
    thunkAPI.dispatch(resetUserContext());
    thunkAPI.dispatch(resetAdminUserContext());
  }
)

export const checkIfUserIsAuthenticated = createAsyncThunk(
  'checkIfUserIsAuthenticated',
  async (param: void, thunkAPI) => {
    const checkUserSession = await authService.checkForUsersession();
    const getProfileResult =  getDefaultUserContextState();


    if (checkUserSession?.isAuthenticated) {
      const userProfileStatus = await userService.getUserProfileWrapper({thunkAPI});

      // When Profile is missing log user out 
      if (userProfileStatus.userProfileNotFound) {
        thunkAPI.dispatch(logUserOut());
    } else {
        getProfileResult.userProfile = userProfileStatus.userProfile;
        getProfileResult.userIsAuthenticated = true;
        getProfileResult.onLoginScreen = false;
        getProfileResult.homeRoute = userProfileStatus.userProfile.defaultRoute;
        getProfileResult.clearForm = true;
      }

      if(userProfileStatus.userProfile.userRole.userRoleId !== 4) {
        thunkAPI.dispatch(getClients({
          includeFacility: true,
        }))
      }
    }
    return getProfileResult;
  }
)


export const updatePassword = createAsyncThunk(
  'updatePassword',
  async (newPassword: string, thunkAPI) => {

    const response = await authService.SetPassword(newPassword);

      // After Password is updated always ensure user is logged out.
      thunkAPI.dispatch(logUserOut());

    return response;
  }
)

export const sendForgetPasswordNotification = createAsyncThunk(
  'sendForgetPasswordNotification',
  async (params: {username: string}) => {

    const {username} = params;

    const response = await authService.sendChangePasswordNotification(params);
    
    if (!response.passwordUpdated) {
      // Return User to the Login Screen if error occurred sending notification
      changeAuthState(AuthView.SignIn);
    }

    return username;
  }
)

export const sendForgetPasswordNotificationV2 = createAsyncThunk(
    'sendForgetPasswordNotificationV2',
    async (params: {username: string, userRoleId: number}, thunkAPI) => {

      const {username} = params;

      const response = await authService.sendChangePasswordNotification(params);

      if(!response.passwordUpdated){
        thunkAPI.dispatch(showStatus("Error while sending password change notification"));
      } else {
        thunkAPI.dispatch(showStatus("Invitation sent successfully "));
      }

      return username;
    }
)

export const updatePasswordWithVerificationCode = createAsyncThunk(
  'updatePasswordWithVerificationCode',
  async (changePasswordState: ChangePasswordState, thunkAPI) => {
    const rootState = thunkAPI.getState() as RootState;
    const userName = rootState.userContext.userEmail;
    const response = await authService.SetPasswordWithVerificationCode(userName, changePasswordState);

    // Return User to the Login Screen after updating password
    changeAuthState(AuthView.SignIn);
    return response;
  }
)

export const updatePasswordWithVerificationCodev2 = createAsyncThunk(
    'updatePasswordWithVerificationCodev2',
    async (changePasswordState: ChangePasswordState) => {
      const userName = changePasswordState.email
      return await authService.SetPasswordWithVerificationCode(userName!,
          changePasswordState);
    }
)

export const verifySigninCode = createAsyncThunk(
  'verifySigninCode',
  async (code: string, thunkAPI) => {
    const verificationCodeStatus = await authService.verifySigninCode(code);
    const loginResult =  getDefaultUserContextState();

    // When the user has been authenticated trigger call to get user profile
    if (verificationCodeStatus) {
      const userProfileStatus = await userService.getUserProfileWrapper({thunkAPI});
      // When Profile is missing log user out and show error
      if (userProfileStatus.userProfileNotFound) {
        thunkAPI.dispatch(logUserOut());
        loginResult.applicationStatus = GetError(GENERIC_AUTHENICATION_ERROR);
      } else {
        loginResult.userProfile = userProfileStatus.userProfile;
        loginResult.userIsAuthenticated = true;
        loginResult.onLoginScreen = false;
        loginResult.homeRoute = userProfileStatus.userProfile.defaultRoute;
        loginResult.clearForm = true;
      }
    } else {
      // Return User to the Login Screen if error occurred sending notification
      loginResult.clearForm = true;
      loginResult.applicationStatus = GetError(VERIFICATIONCODE_AUTHENICATION_ERROR);
      thunkAPI.dispatch(logUserOut());
    }

    return loginResult;
  }
)